import { Card, CardContent } from '@up2stay/up2promo-ui';
import { motion, useReducedMotion } from 'motion/react';

import { WorkspaceDetailsForm } from '@/core/components/workspace/create/workspace-details-form-simple';
import { useDictionary } from '@/core/providers/dictionary-provider';

import { ONBOARDING_LAYOUT, onboardingSpring } from './onboarding-motion';

/** Profiles SoT step title — matches Create Location wizard. */
const STEP_TITLE_CLASS =
  'text-balance text-[22px] font-bold leading-tight text-foreground sm:text-[24px]';

const STEP_BODY_CLASS =
  'text-pretty text-[13.5px] leading-relaxed text-muted-foreground';

interface OnboardingWorkspaceStepProps {
  mode: 'onboarding' | 'create';
  name: string;
  onNameChange: (value: string) => void;
  username: string;
  onUsernameChange: (value: string) => void;
  description: string;
  onDescriptionChange: (value: string) => void;
  photoPreview?: string;
  onPhotoChange: (
    fileId: string | undefined,
    preview: string | undefined,
  ) => void;
  nameError: string | null;
  usernameError: string | null;
  createError?: string | null;
  isCreating?: boolean;
}

export function OnboardingWorkspaceStep({
  mode,
  name,
  onNameChange,
  username,
  onUsernameChange,
  description,
  onDescriptionChange,
  photoPreview,
  onPhotoChange,
  nameError,
  usernameError,
  createError,
  isCreating,
}: OnboardingWorkspaceStepProps) {
  const dict = useDictionary();
  const reduceMotion = useReducedMotion();
  const copy =
    mode === 'create'
      ? dict.onboarding.workspace.createMode
      : dict.onboarding.workspace;

  return (
    <div
      className="mx-auto flex w-full max-w-lg flex-col gap-5"
      aria-busy={isCreating || undefined}
    >
      <div className="flex flex-col gap-2 text-center md:text-start">
        <h2 className={STEP_TITLE_CLASS}>{copy.title}</h2>
        <p className={STEP_BODY_CLASS}>{copy.description}</p>
      </div>

      <motion.div
        layoutId={reduceMotion ? undefined : ONBOARDING_LAYOUT.primarySurface}
        transition={onboardingSpring}
      >
        <Card className="min-w-0 overflow-hidden">
          <CardContent className="flex flex-col gap-6 p-5 sm:p-6">
            <WorkspaceDetailsForm
              name={name}
              onNameChange={onNameChange}
              username={username}
              onUsernameChange={onUsernameChange}
              description={description}
              onDescriptionChange={onDescriptionChange}
              photoPreview={photoPreview}
              onPhotoChange={onPhotoChange}
              nameError={nameError}
              usernameError={usernameError}
            />
          </CardContent>
        </Card>
      </motion.div>

      {createError ? (
        <div
          role="alert"
          className="rounded-[var(--r-control)] border border-destructive/30 bg-destructive/5 px-4 py-3 text-[13.5px] text-destructive"
        >
          {createError}
        </div>
      ) : null}
    </div>
  );
}
